import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../redux/actions/authActions';
import { 
  FaWarehouse, 
  FaBoxes, 
  FaTruck, 
  FaClipboardList,
  FaUserCircle,
  FaSignOutAlt,
  FaSearch,
  FaPlus,
  FaLink,
  FaTerminal
} from 'react-icons/fa';
import './InboundOrder.css';
import { fetchOrders, fetchOrder, assignOrderToLoad } from '../redux/actions/orderActions';
import { fetchLoad, createLoad } from '../redux/actions/loadActions';
import axios from 'axios';

const InboundOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    orders, 
    pagination, 
    currentOrder, 
    isLoading,
    error 
  } = useSelector(state => state.order);
  const { currentLoad, isLoading: loadLoading, error: loadError } = useSelector(state => state.load);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [loadNumber, setLoadNumber] = useState('');
  const [showCreateLoad, setShowCreateLoad] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [newLoadForm, setNewLoadForm] = useState({
    loadNumber: '',
    carrier: '',
    driverName: '',
    trailerNumber: '',
    expectedArrival: '',
    dock: '',
    notes: ''
  });
  const [searchOrderId, setSearchOrderId] = useState('');

  useEffect(() => {
    console.log('Fetching orders...');
    loadOrders(currentPage);
  }, [dispatch, currentPage]);

  const loadOrders = async (page) => {
    try {
      await dispatch(fetchOrders(page));
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const handleOrderSelect = async (orderId) => {
    setSelectedOrderId(orderId);
    try {
      await dispatch(fetchOrder(orderId));
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = async () => {
    if (searchOrderId.trim()) {
      try {
        await dispatch(fetchOrder(searchOrderId));
        setSelectedOrderId(searchOrderId);
      } catch (error) {
        console.error('Error searching order:', error);
      }
    }
  };

  const handleLoadSearch = async () => {
    try {
      await dispatch(fetchLoad(loadNumber));
      await dispatch(assignOrderToLoad(selectedOrderId, loadNumber));
      setShowLoadModal(false);
      loadOrders(currentPage);
      setSelectedOrderId(selectedOrderId)
      await dispatch(fetchOrder(selectedOrderId));
    } catch (error) {
      if (error.response?.status === 404) {
        alert('Load number not found. Please create a new load.');
        setShowCreateLoad(true);
      } else {
        alert('Error assigning load: ' + error.response?.data?.message);
      }
    }
  };

  const handleCreateLoad = async (e) => {
    e.preventDefault();
    try {
      const loadData = {
        ...newLoadForm,
        orders: [selectedOrderId]
      };
      await dispatch(createLoad(loadData));
      setShowCreateLoad(false);
      setShowLoadModal(false);
      alert('Load created successfully');
    } catch (error) {
      alert('Failed to create load: ' + error.response?.data?.message);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleCloseOrderModal = () => {
    setSelectedOrderId(null);
  };

  const handleCloseLoadModal = () => {
    setShowLoadModal(false);
    setLoadNumber('');
  };

  const handleCloseCreateLoadModal = () => {
    setShowCreateLoad(false);
    setNewLoadForm({
      loadNumber: '',
      carrier: '',
      driverName: '',
      trailerNumber: '',
      expectedArrival: '',
      dock: '',
      notes: ''
    });
  };

  return (
    <div className="inbound-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <FaWarehouse className="sidebar-icon" />
          <span>WMS System</span>
        </div>
        <nav className="sidebar-nav">
          <Link to="/inbound-order" className="active"><FaBoxes /> Inbound Orders</Link>
          <Link to="/rf"><FaTerminal /> RF Terminal</Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Navigation */}
        <header className="top-nav">
          <div className="search-container">
            <input 
              type="text"
              placeholder="Search Order ID..."
              className="search-input"
              value={searchOrderId}
              onChange={(e) => setSearchOrderId(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button 
              className="search-btn"
              onClick={handleSearch}
            >
              <FaSearch />
            </button>
          </div>
          <div className="user-nav">
            <span className="user-name">
              <FaUserCircle /> John Doe
            </span>
            <button onClick={handleLogout} className="logout-btn">
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="page-content">
          <div className="content-header">
            <h1>Inbound Order Management</h1>
            {/* Pagination moved to top right */}
            {pagination && pagination.totalPages > 1 && (
              <div className="pagination-container">
                <button
                  className="btn btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  disabled={currentPage === pagination.totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center my-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Orders Table */}
          <div className="orders-section card">
            <div className="card-header">
              <h2>Orders List</h2>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Supplier</th>
                      <th>Order Date</th>
                      <th>Status</th>
                      <th>Load Number</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders && orders.length > 0 ? (
                      orders.map(order => (
                        <tr 
                          key={order.orderId}
                          className={selectedOrderId === order.orderId ? 'selected' : ''}
                        >
                          <td>{order.orderId}</td>
                          <td>{order.supplier}</td>
                          <td>{new Date(order.orderDate).toLocaleDateString()}</td>
                          <td>
                            <span className={`status-badge ${order.status.toLowerCase()}`}>
                              {order.status}
                            </span>
                          </td>
                          <td>{order.loadNumber || 'Not Assigned'}</td>
                          <td>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleOrderSelect(order.orderId)}
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Order Details Modal */}
          {currentOrder && selectedOrderId && (
            <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Order Details</h2>
                  <button 
                    className="close-btn"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents the modal overlay click from firing
                      handleCloseOrderModal();
                    }}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="order-info-grid">
                    <div className="info-group">
                      <label>Order ID</label>
                      <span>{currentOrder.orderId}</span>
                    </div>
                    <div className="info-group">
                      <label>Supplier</label>
                      <span>{currentOrder.supplier}</span>
                    </div>
                    <div className="info-group">
                      <label>Order Date</label>
                      <span>{new Date(currentOrder.orderDate).toLocaleDateString()}</span>
                    </div>
                    <div className="info-group">
                      <label>Status</label>
                      <span className={`status-badge`}>
                        {currentOrder.status}
                      </span>
                    </div>
                  </div>

                  {/* Line Items Table */}
                  <div className="line-items mt-4">
                    <h3>Line Items</h3>
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Item Id</th>
                            <th>Description</th>
                            <th>Quantity</th>
                            <th>Unit</th>
                            <th>Unit Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentOrder.lineItems.map(item => (
                            <tr key={item.id}>
                              <td>{item.sku}</td>
                              <td>{item.description}</td>
                              <td>{item.quantity}</td>
                              <td>{item.unit}</td>
                              <td>${item.price.toFixed(2)}</td>
                              <td>${(item.quantity * item.price).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Load Assignment Section */}
                  <div className="load-assignment mt-4">
                    <h3>Load Assignment</h3>
                    {currentOrder.loadNumber ? (
                      <div className="load-info">
                        <FaTruck className="me-2" />
                        Load: {currentOrder.loadNumber}
                      </div>
                    ) : (
                      <button 
                        className="btn btn-primary"
                        onClick={() => setShowLoadModal(true)}
                      >
                        <FaLink className="me-2" />
                        Assign to Load
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assign Load Modal */}
          {showLoadModal && (
            <div className="modal-overlay" onClick={handleCloseLoadModal}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Assign to Load</h2>
                  <button 
                    className="close-btn"
                    onClick={handleCloseLoadModal}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Enter Load Number</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={loadNumber}
                        onChange={(e) => setLoadNumber(e.target.value)}
                        placeholder="Enter existing load number"
                      />
                      <button 
                        className="btn btn-primary"
                        onClick={handleLoadSearch}
                      >
                        Search & Assign Load
                      </button>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <p>- OR -</p>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowCreateLoad(true)}
                    >
                      <FaPlus className="me-2" />
                      Create New Load
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Load Modal */}
          {showCreateLoad && (
            <div className="modal-overlay" onClick={handleCloseCreateLoadModal}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Create New Load</h2>
                  <button 
                    className="close-btn"
                    onClick={handleCloseCreateLoadModal}
                  >
                    ×
                  </button>
                </div>
                <style jsx="true">
                  {`
                    .modal-content .form-control {
                      border: 1px solid #ced4da !important;
                      background-color: #fff !important;
                      color: #212529 !important;
                      box-shadow: 0 0 0 1px #ced4da;
                    }
                    .modal-content .input-group-text {
                      background-color: #f8f9fa !important;
                      border: 1px solid #ced4da !important;
                    }
                    .modal-content .form-label {
                      font-weight: 500;
                      margin-bottom: 0.5rem;
                      color: #495057;
                    }
                    .modal-content .form-group {
                      margin-bottom: 1rem;
                    }
                  `}
                </style>
                <form onSubmit={handleCreateLoad} className="create-load-form">
                  <div className="modal-body">
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Load Number</label>
                          <input
                            type="text"
                            className="form-control"
                            value={newLoadForm.loadNumber}
                            onChange={(e) => setNewLoadForm({...newLoadForm, loadNumber: e.target.value})}
                            placeholder="Enter load number"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Carrier</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={newLoadForm.carrier}
                              onChange={(e) => setNewLoadForm({...newLoadForm, carrier: e.target.value})}
                              placeholder="Enter carrier name"
                              required
                            />
                            <span className="input-group-text">
                              <FaBoxes />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Driver Name</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={newLoadForm.driverName}
                              onChange={(e) => setNewLoadForm({...newLoadForm, driverName: e.target.value})}
                              placeholder="Enter driver name"
                            />
                            <span className="input-group-text">
                              <FaUserCircle />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Trailer Number</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={newLoadForm.trailerNumber}
                              onChange={(e) => setNewLoadForm({...newLoadForm, trailerNumber: e.target.value})}
                              placeholder="Enter trailer number"
                              required
                            />
                            <span className="input-group-text">
                              <FaWarehouse />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Expected Arrival</label>
                          <div className="input-group">
                            <input
                              type="datetime-local"
                              className="form-control"
                              value={newLoadForm.expectedArrival}
                              onChange={(e) => setNewLoadForm({...newLoadForm, expectedArrival: e.target.value})}
                              required
                            />
                            <span className="input-group-text">
                              <FaClipboardList />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Dock</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={newLoadForm.dock}
                              onChange={(e) => setNewLoadForm({...newLoadForm, dock: e.target.value})}
                              placeholder="Enter dock number"
                            />
                            <span className="input-group-text">
                              <FaTerminal />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-group mb-3">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        value={newLoadForm.notes}
                        onChange={(e) => setNewLoadForm({...newLoadForm, notes: e.target.value})}
                        rows="1"
                        placeholder="Enter any additional notes"
                      ></textarea>
                    </div>
                  </div>
                  <div className="modal-footer d-flex justify-content-center">
                    <button 
                      type="button" 
                      className="btn btn-outline-secondary" 
                      onClick={() => setShowCreateLoad(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      <FaPlus className="me-2" />
                      Create Load 
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InboundOrder; 